import { useEffect, useMemo, useState } from '../../vendor/react';
import type { AiAnswer, ClosureIssuePolicy, ClosureScope } from '../../types/domain';
import { formatMoney } from '../../lib/money';
import { usePhase9 } from '../phase9/usePhase9';
import { buildClosureIssues, buildClosureSummary, inventoryValue, previousMonthRange } from './closure-engine';
import {
  closeDailyClosure,
  closeMonthlyClosure,
  ensurePhase10,
  getPhase10,
  prepareDailyClosure,
  prepareMonthlyClosure,
  reopenClosure,
  resolveClosureIssue,
  saveClosurePolicy,
  subscribePhase10,
} from './phase10-store';

const monthRange = (month: string) => ({ from: `${month}-01`, to: new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0, 12)).toISOString().slice(0, 10) });
const today = () => new Date().toISOString().slice(0, 10);

export function usePhase10() {
  const phase9 = usePhase9();
  const businessId = phase9.business?.id ?? '';
  const currentBranchId = phase9.branch?.id ?? '';
  const userId = phase9.session?.user.id ?? 'unknown';
  const [revision, setRevision] = useState(0);
  useEffect(() => subscribePhase10(() => setRevision((value) => value + 1)), []);
  useEffect(() => { if (businessId) ensurePhase10(businessId); }, [businessId]);
  const snapshot = useMemo(() => businessId ? getPhase10(businessId) ?? ensurePhase10(businessId) : null, [businessId, revision]);
  const buildInput = (from: string, to: string, scope: ClosureScope, branchId?: string) => {
    if (!phase9.sales.snapshot || !phase9.catalog || !phase9.snapshot || !phase9.phase8) throw new Error('No hay datos suficientes para preparar el cierre.');
    return { businessId, branchId: scope === 'branch' ? branchId || currentBranchId : undefined, scope, from, to, branches: phase9.branches, sales: phase9.sales.snapshot, catalog: phase9.catalog, operations: phase9.snapshot, phase8: phase9.phase8, receivables: phase9.receivables, payables: phase9.payables };
  };
  const snapshotAmounts = (branchId?: string) => ({
    inventoryValueCents: phase9.catalog ? inventoryValue(phase9.catalog, branchId) : 0,
    receivableCents: phase9.receivables.filter((item) => !branchId || item.branchId === branchId).reduce((sum, item) => sum + item.balanceCents, 0),
    payableCents: phase9.payables.filter((item) => !branchId || item.branchId === branchId).reduce((sum, item) => sum + item.balanceCents, 0),
    membershipReceivableCents: phase9.phase8?.membershipCharges.filter((item) => ['pending', 'overdue'].includes(item.status)).reduce((sum, item) => sum + item.amountCents - item.paidCents, 0) ?? 0,
  });
  return {
    ...phase9,
    phase10: snapshot,
    dailyClosures: snapshot?.dailyClosures ?? [],
    monthlyClosures: snapshot?.monthlyClosures ?? [],
    closureIssues: snapshot?.issues ?? [],
    closureSnapshots: snapshot?.snapshots ?? [],
    businessPeriods: snapshot?.periods ?? [],
    closurePolicy: snapshot?.issuePolicy ?? 'authorize',
    actions10: {
      setPolicy: (policy: ClosureIssuePolicy) => saveClosurePolicy(businessId, policy),
      prepareDaily: (input: { date?: string; scope: ClosureScope; branchId?: string; notes?: string }) => {
        const date = input.date ?? today();
        const engineInput = buildInput(date, date, input.scope, input.branchId);
        const summary = buildClosureSummary(engineInput);
        const issues = buildClosureIssues(engineInput, 'pending', summary);
        const closure = prepareDailyClosure(businessId, { branchId: engineInput.branchId, scope: input.scope, date, issuePolicy: snapshot?.issuePolicy ?? 'authorize', summary, notes: input.notes?.trim() ?? '', preparedBy: userId, issues });
        phase9.actions.audit({ branchId: engineInput.branchId, entityType: 'closure', entityId: closure.id, action: 'closure.daily.prepared', summary: `${closure.folio} preparado para ${date}.`, metadata: { scope: input.scope, issues: issues.length } });
        return closure;
      },
      prepareMonthly: (input: { month: string; scope: ClosureScope; branchId?: string; notes?: string }) => {
        const range = monthRange(input.month);
        const engineInput = buildInput(range.from, range.to, input.scope, input.branchId);
        const summary = buildClosureSummary(engineInput);
        const issues = buildClosureIssues(engineInput, 'pending', summary);
        const previousRange = previousMonthRange(input.month);
        const previousSummary = buildClosureSummary(buildInput(previousRange.from, previousRange.to, input.scope, input.branchId));
        const comparison = previousSummary.salesCents > 0 ? Math.round(((summary.salesCents - previousSummary.salesCents) / previousSummary.salesCents) * 1000) / 10 : undefined;
        const amounts = snapshotAmounts(engineInput.branchId);
        const closure = prepareMonthlyClosure(businessId, { branchId: engineInput.branchId, scope: input.scope, month: input.month, issuePolicy: snapshot?.issuePolicy ?? 'authorize', summary, notes: input.notes?.trim() ?? '', preparedBy: userId, issues, comparisonPreviousMonthPercent: comparison, inventoryValueCents: amounts.inventoryValueCents, receivableCents: amounts.receivableCents, payableCents: amounts.payableCents });
        phase9.actions.audit({ branchId: engineInput.branchId, entityType: 'closure', entityId: closure.id, action: 'closure.monthly.prepared', summary: `${closure.folio} preparado para ${input.month}.`, metadata: { scope: input.scope, issues: issues.length } });
        return closure;
      },
      closeDaily: (closureId: string, authorizationReason?: string) => {
        const closure = snapshot?.dailyClosures.find((item) => item.id === closureId);
        const amounts = snapshotAmounts(closure?.branchId);
        const result = closeDailyClosure(businessId, closureId, { userId, authorizationReason, ...amounts });
        phase9.actions.audit({ branchId: result.branchId, entityType: 'period', entityId: result.id, action: 'period.daily.closed', summary: `${result.folio} cerrado.`, metadata: { date: result.date, hash: result.snapshotId ?? '' } });
        return result;
      },
      closeMonthly: (closureId: string, authorizationReason?: string) => {
        const closure = snapshot?.monthlyClosures.find((item) => item.id === closureId);
        const amounts = snapshotAmounts(closure?.branchId);
        const result = closeMonthlyClosure(businessId, closureId, { userId, authorizationReason, ...amounts });
        phase9.actions.audit({ branchId: result.branchId, entityType: 'period', entityId: result.id, action: 'period.monthly.closed', summary: `${result.folio} cerrado.`, metadata: { month: result.month, hash: result.snapshotId ?? '' } });
        return result;
      },
      reopen: (type: 'daily' | 'monthly', closureId: string, reason: string) => {
        const result = reopenClosure(businessId, type, closureId, { userId, reason });
        phase9.actions.audit({ branchId: result.branchId, entityType: 'period', entityId: result.id, action: `period.${type}.reopened`, summary: `${result.folio} reabierto: ${reason}`, metadata: { reason } });
        return result;
      },
      resolveIssue: (issueId: string, resolution: string) => resolveClosureIssue(businessId, issueId, { userId, resolution }),
      askNexo: (question: string): AiAnswer => {
        const text = question.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (text.includes('cierre') || text.includes('caja abierta') || text.includes('periodo')) {
          const latest = snapshot?.dailyClosures[0];
          const pending = snapshot?.dailyClosures.filter((item) => !['closed', 'closed-again'].includes(item.status)).length ?? 0;
          const closedMonth = snapshot?.monthlyClosures.find((item) => ['closed', 'closed-again'].includes(item.status));
          const answer = latest ? `El último cierre preparado es ${latest.folio} del ${latest.date}. Tiene ${latest.summary.openCashSessions} cajas abiertas, una diferencia de ${formatMoney(latest.summary.cashDifferenceCents)} y estado ${latest.status}. Hay ${pending} cierres diarios pendientes.${closedMonth ? ` El último mes cerrado es ${closedMonth.month}.` : ''}` : 'Todavía no hay cierres preparados. Puedes generar el cierre diario desde el módulo Cierres.';
          const result: AiAnswer = { id: `aia_${crypto.randomUUID?.() ?? Date.now().toString(36)}`, question, answer, generatedAt: new Date().toISOString(), toolsUsed: ['closures.latest', 'closures.issues', 'periods.locked'], insights: [] };
          phase9.actions.audit({ branchId: currentBranchId, entityType: 'ai', entityId: result.id, action: 'ai.question', summary: question, metadata: { tools: result.toolsUsed.join(',') } });
          return result;
        }
        return phase9.actions9.askNexo(question);
      },
    },
  };
}
